import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import ProcessTimeline from "../components/ProcessTimeline";
import { Reveal } from "../components/Reveal";
import { FinalCTA } from "./Home";
import { PROCESS_PHASES, IMAGES } from "../data/content";

const OverlapDiagram = () => (
  <div data-testid="overlap-diagram" className="relative border border-white/10 p-8 sm:p-14">
    <p className="label-tech text-paper/50 mb-10">How the disciplines overlap</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
      {[
        { name: "Architecture", span: "sm:translate-x-6" },
        { name: "Construction", span: "sm:-translate-x-6 sm:translate-y-[-8px]" },
        { name: "Procurement", span: "sm:translate-x-6 sm:translate-y-[-16px]" },
        { name: "Project Management", span: "sm:-translate-x-6 sm:translate-y-[-24px] border-electric/60" },
      ].map((d) => (
        <div key={d.name} className={`border border-paper/25 bg-coal/60 backdrop-blur-sm px-6 py-8 text-center ${d.span}`}>
          <span className="label-tech text-paper">{d.name}</span>
        </div>
      ))}
    </div>
    <p className="text-center mt-10 text-sm text-paper/60">
      <span className="text-electric font-semibold">One coordinated process</span> — every discipline informs the others, continuously.
    </p>
  </div>
);

const PhaseRow = ({ phase, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <Reveal i={i % 3}>
      <div className="border-t border-ink/10" data-testid={`phase-${phase.name.toLowerCase()}`}>
        <button
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          data-testid={`phase-toggle-${phase.name.toLowerCase()}`}
          className="w-full grid grid-cols-12 gap-4 items-center py-7 sm:py-9 text-left group min-h-[44px]"
        >
          <span className="col-span-3 sm:col-span-2 font-serif italic text-4xl sm:text-5xl text-ink/15 group-hover:text-cobalt transition-colors duration-500">{phase.number}</span>
          <span className="col-span-7 sm:col-span-8 t-h2 text-coal">{phase.name}</span>
          <span className="col-span-2 flex justify-end text-graphite">
            {open ? <Minus size={22} /> : <Plus size={22} />}
          </span>
        </button>
        <div className={`grid grid-cols-12 gap-8 overflow-hidden transition-[max-height,opacity,padding] duration-700 ease-expo ${open ? "max-h-[1200px] opacity-100 pb-12" : "max-h-0 opacity-0"}`}>
          <div className="col-span-12 lg:col-span-4 lg:col-start-3">
            <p className="label-tech text-graphite mb-4">Objective</p>
            <p className="t-intro text-coal !text-[clamp(1.25rem,2.2vw,1.8rem)]">{phase.objective}</p>
          </div>
          <div className="col-span-12 sm:col-span-6 lg:col-span-3">
            <p className="label-tech text-graphite mb-4">Principal activities</p>
            <ul className="space-y-2.5">
              {phase.activities.map((a) => (
                <li key={a} className="text-[15px] text-coal flex items-baseline gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-cobalt shrink-0 translate-y-[-2px]" />{a}
                </li>
              ))}
            </ul>
            <p className="label-tech text-graphite mt-8 mb-4">Key deliverables</p>
            <ul className="space-y-2.5">
              {phase.deliverables.map((d) => (
                <li key={d} className="text-[15px] text-graphite flex items-baseline gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-ink/30 shrink-0 translate-y-[-2px]" />{d}
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-12 sm:col-span-6 lg:col-span-3">
            <p className="label-tech text-graphite mb-4">Decision points</p>
            <p className="text-[15px] text-coal leading-relaxed">{phase.decisions}</p>
            <p className="label-tech text-graphite mt-8 mb-4">Client involvement</p>
            <p className="text-[15px] text-graphite leading-relaxed">{phase.client}</p>
          </div>
        </div>
      </div>
    </Reveal>
  );
};

const Approach = () => (
  <>
    <SEO title="Approach | ARCH EXPO" description="The ARCH EXPO method: seven project phases — Discover, Define, Design, Plan, Source, Build, Deliver — in one coordinated framework." />
    <PageHero
      label="Approach"
      titleLines={["One method.", "Seven phases."]}
      intro="A disciplined framework that carries a project from first decision to final handover — with design, construction and procurement coordinated at every step."
    />
    <section className="bg-paper pb-20" data-testid="approach-overview">
      <div className="container-ax grid grid-cols-12 gap-8 items-center">
        <div className="col-span-12 lg:col-span-7">
          <div className="border border-ink/10">
            <ProcessTimeline phases={PROCESS_PHASES} compact={false} />
          </div>
        </div>
        <div className="col-span-12 lg:col-span-4 lg:col-start-9">
          <Reveal>
            <p className="t-intro text-coal mb-6">From first decision to final handover.</p>
            <p className="text-graphite text-[15px] leading-relaxed">
              Each phase has defined objectives, deliverables and decision points — so progress is visible, budgets stay honest and quality is never negotiated downstream.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
    <section className="section-pad bg-coal text-paper grid-lines-dark" data-testid="approach-diagram">
      <div className="container-ax">
        <SectionHeading dark number="01" label="Integration" title="Disciplines that overlap by design." />
        <Reveal>
          <OverlapDiagram />
        </Reveal>
      </div>
    </section>
    <section className="section-pad bg-paper" data-testid="approach-phases">
      <div className="container-ax">
        <SectionHeading number="02" label="The Phases" title="Phase by phase." />
        <div className="border-b border-ink/10">
          {PROCESS_PHASES.map((p, i) => (
            <PhaseRow key={p.number} phase={p} i={i} />
          ))}
        </div>
      </div>
    </section>
    <section className="bg-paper pb-24 sm:pb-32">
      <div className="container-ax">
        <Reveal className="overflow-hidden">
          <img src={IMAGES.approach} alt="Architectural planning drawings on a table (placeholder image)" loading="lazy" className="w-full aspect-[16/7] object-cover" />
        </Reveal>
      </div>
    </section>
    <FinalCTA />
  </>
);

export default Approach;
